import React, { useState } from 'react';
import { Box } from '@mui/material';
import Title from '../../../component/title/Title';
import CloseButton from '../../../image/xButton.svg';
import { SecondaryButton, IOSSwitch } from '../../../component/buttonComponent/ButtonComponent';
import { useManageVehicleTypes } from './useVehicleTypeOptions';
import { formatVehicleClassLabel } from './vehicleTypeOptions';
import './vehicleTypeManager.css';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 520,
    bgcolor: '#FFFFFF',
    borderRadius: '12px',
};

/**
 * System Administrator dialog for activating and deactivating vehicle types.
 *
 * Vehicle types are never renamed or deleted — the name is persisted as a free string on vehicle
 * presets and snapshotted onto device documents, so either would orphan live data. Deactivating is
 * the escape hatch for a typo: the type stops being offered for new selections while presets and
 * devices already using it keep displaying it. The seeded built-ins cannot be deactivated at all,
 * since every existing preset references them.
 */
const VehicleTypeManagerModal = ({ onClose }) => {
    const {
        vehicleTypes, loading, error, setVehicleTypeActive,
    } = useManageVehicleTypes(true);
    // Per-row, so one failing toggle neither blanks the others nor disables every switch at once.
    const [pendingOid, setPendingOid] = useState(null);
    const [actionError, setActionError] = useState('');

    const handleToggle = async (type) => {
        setPendingOid(type.oid);
        setActionError('');
        try {
            await setVehicleTypeActive(type.oid, !(type.isActive !== false));
        } catch (e) {
            setActionError(e?.message || 'Could not update the vehicle type');
        } finally {
            setPendingOid(null);
        }
    };

    const loadError = error ? (error.message || 'Could not load vehicle types') : '';

    const renderList = () => {
        if (loading) return <div className="vtm-empty">Loading vehicle types…</div>;
        if (loadError) return <div className="vtm-error">{loadError}</div>;
        if (vehicleTypes.length === 0) return <div className="vtm-empty">No vehicle types found.</div>;
        return (
            <div className="vtm-list">
                {vehicleTypes.map((type) => {
                    const isActive = type.isActive !== false;
                    const isBuiltIn = type.isSystem === true;
                    return (
                        <div className="vtm-row" key={type.oid}>
                            <div className="vtm-row-text">
                                <div className="vtm-row-name">
                                    {type.name}
                                    {!isActive && <span className="vtm-badge">Inactive</span>}
                                </div>
                                <div className="vtm-row-desc">
                                    {type.vehicleClass
                                        ? `Class · ${formatVehicleClassLabel(type.vehicleClass)}`
                                        : null}
                                    {type.vehicleClass && (isBuiltIn || type.description) ? ' · ' : null}
                                    {isBuiltIn
                                        ? 'Always available — every preset can use it'
                                        : (type.description || (type.vehicleClass ? '' : '—'))}
                                </div>
                            </div>
                            {isBuiltIn ? (
                                // A switch that can never move reads as broken, so built-ins get a
                                // static badge instead. (IOSSwitch does style its disabled state; this
                                // is a UX choice, not a styling gap.)
                                <span className="vtm-builtin">Built-in</span>
                            ) : (
                                <IOSSwitch
                                  checked={isActive}
                                  disabled={pendingOid === type.oid}
                                  onChange={() => handleToggle(type)}
                                  inputProps={{ 'aria-label': `Toggle ${type.name}` }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <Box sx={style} role="dialog" aria-modal="true" aria-label="Manage vehicle types">
            {/* Title sits BELOW the close row, not beside it: .title-container is flex-shrink: 0, so as a
                flex sibling of .x-button it cannot shrink and long copy overflows the dialog. This is
                the same structure DeleteModal uses. */}
            <div className="modal-header-content">
                <div className="button-container">
                    <div
                      type="button"
                      role="button"
                      className="x-button"
                      tabIndex="0"
                      onKeyUp={() => onClose()}
                      onClick={() => onClose()}
                    >
                        <img loading="lazy" style={{ position: 'relative', bottom: 15, left: 9 }} src={CloseButton} alt="close-modal" />
                    </div>
                </div>
                <Title
                  title="Manage vehicle types"
                  subtitle="Body types are grouped by vehicle class. Deactivating a type stops it being offered on new presets. Presets and vehicles already using it keep their current type."
                  titleSize="1.125rem"
                  subtitleSize="0.875rem"
                  gap="0.25rem"
                  lineHeight="1.75rem"
                />
            </div>

            <div className="vtm-body">
                {renderList()}
                {actionError && <div className="vtm-error vtm-error-action">{actionError}</div>}
            </div>

            <div className="vtm-footer">
                <SecondaryButton label="Close" width="170px" imageIcon={false} onClickFunction={() => onClose()} />
            </div>
        </Box>
    );
};

export default VehicleTypeManagerModal;
